/// <reference path="./imageFilter.d.ts" />
import { PdfGen, PdfImage, PdfColor } from '../pdf';
import type { IImageCanvas } from '../imageCanvas';

/**
 * Image filter prototype methods for PdfImage.
 *
 * Filters use the stored IImageCanvas (decoded RGBA pixels) to apply
 * a transformation, encode to PNG, and re-embed in the PDF.
 *
 * PdfImage.canvas must be present (it is set automatically when PdfImage
 * is created via RawImage.embed() or preprocessImages()).
 */

function requireCanvas(img: PdfImage, filterName: string): IImageCanvas {
  if (!img.canvas) {
    throw new Error(
      `PdfImage.${filterName}() requires a canvas — the image must have been embedded ` +
        `via RawImage.embed() or preprocessImages(). ` +
        `Images embedded directly with pdf.pdf.embedPng() have no canvas.`
    );
  }
  return img.canvas;
}

PdfImage.prototype.toShadow = async function (pdf: PdfGen): Promise<PdfImage> {
  const canvas = requireCanvas(this, 'toShadow');
  const p = new Uint8Array(canvas.getRgba());
  for (let i = 0; i < p.length; i += 4)
    if (p[i + 3] > 10) {
      p[i] = 0;
      p[i + 1] = 0;
      p[i + 2] = 0;
    }
  const newCanvas = canvas.withRgba(p);
  const embedded = await pdf.pdf.embedPng(newCanvas.toPng());
  return new PdfImage(embedded, embedded.width, embedded.height, newCanvas);
};

PdfImage.prototype.flipH = async function (pdf: PdfGen): Promise<PdfImage> {
  const canvas = requireCanvas(this, 'flipH');
  const { width, height } = canvas;
  const p = new Uint8Array(canvas.getRgba());
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < Math.floor(width / 2); x++) {
      const li = (y * width + x) * 4;
      const ri = (y * width + (width - 1 - x)) * 4;
      for (let c = 0; c < 4; c++) {
        const t = p[li + c];
        p[li + c] = p[ri + c];
        p[ri + c] = t;
      }
    }
  }
  const newCanvas = canvas.withRgba(p);
  const embedded = await pdf.pdf.embedPng(newCanvas.toPng());
  return new PdfImage(embedded, embedded.width, embedded.height, newCanvas);
};

PdfImage.prototype.flipV = async function (pdf: PdfGen): Promise<PdfImage> {
  const canvas = requireCanvas(this, 'flipV');
  const { width, height } = canvas;
  const p = new Uint8Array(canvas.getRgba());
  for (let y = 0; y < Math.floor(height / 2); y++) {
    for (let x = 0; x < width; x++) {
      const ti = (y * width + x) * 4;
      const bi = ((height - 1 - y) * width + x) * 4;
      for (let c = 0; c < 4; c++) {
        const t = p[ti + c];
        p[ti + c] = p[bi + c];
        p[bi + c] = t;
      }
    }
  }
  const newCanvas = canvas.withRgba(p);
  const embedded = await pdf.pdf.embedPng(newCanvas.toPng());
  return new PdfImage(embedded, embedded.width, embedded.height, newCanvas);
};

PdfImage.prototype.toBlackWhite = async function (pdf: PdfGen): Promise<PdfImage> {
  const canvas = requireCanvas(this, 'toBlackWhite');
  const p = new Uint8Array(canvas.getRgba());
  for (let i = 0; i < p.length; i += 4) {
    const gray = Math.round(0.299 * p[i] + 0.587 * p[i + 1] + 0.114 * p[i + 2]);
    p[i] = gray;
    p[i + 1] = gray;
    p[i + 2] = gray;
  }
  const newCanvas = canvas.withRgba(p);
  const embedded = await pdf.pdf.embedPng(newCanvas.toPng());
  return new PdfImage(embedded, embedded.width, embedded.height, newCanvas);
};

PdfImage.prototype.toOneColor = async function (pdf: PdfGen, color: PdfColor): Promise<PdfImage> {
  const canvas = requireCanvas(this, 'toOneColor');
  const p = new Uint8Array(canvas.getRgba());
  const { r, g, b } = color.getRgb();
  for (let i = 0; i < p.length; i += 4)
    if (p[i + 3] > 10) {
      p[i] = r;
      p[i + 1] = g;
      p[i + 2] = b;
    }
  const newCanvas = canvas.withRgba(p);
  const embedded = await pdf.pdf.embedPng(newCanvas.toPng());
  return new PdfImage(embedded, embedded.width, embedded.height, newCanvas);
};

PdfImage.prototype.toGradient = async function (
  pdf: PdfGen,
  colorLeft: { r: number; g: number; b: number },
  colorRight: { r: number; g: number; b: number }
): Promise<PdfImage> {
  const canvas = requireCanvas(this, 'toGradient');
  const { width, height } = canvas;
  const p = new Uint8Array(canvas.getRgba());
  for (let y = 0; y < height; y++) {
    const d = (y / height - 0.5) * 100;
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      if (p[idx + 3] > 220) {
        const t = x / width;
        p[idx] = Math.min(
          255,
          Math.max(0, Math.round(colorLeft.r + (colorRight.r - colorLeft.r) * t - d))
        );
        p[idx + 1] = Math.min(
          255,
          Math.max(0, Math.round(colorLeft.g + (colorRight.g - colorLeft.g) * t - d))
        );
        p[idx + 2] = Math.min(
          255,
          Math.max(0, Math.round(colorLeft.b + (colorRight.b - colorLeft.b) * t - d))
        );
        p[idx + 3] = 255;
      } else {
        p[idx + 3] = 0;
      }
    }
  }
  const newCanvas = canvas.withRgba(p);
  const embedded = await pdf.pdf.embedPng(newCanvas.toPng());
  return new PdfImage(embedded, embedded.width, embedded.height, newCanvas);
};
